import { Component, Project } from "projen";
import { GitHub } from "projen/lib/github";
import { JobStep } from "projen/lib/github/workflows-model";
import { VERSION } from "../versions";

/**
 * Prefix matched against {@link JobStep.uses} to identify pnpm/action-setup steps.
 */
const PNPM_ACTION_SETUP_PREFIX = "pnpm/action-setup@";

/**
 * Component property names that, when present, hold an array of
 * {@link JobStep} objects we want to scan for pnpm/action-setup pins.
 *
 * Projen's {@code BuildWorkflow} stores them on a private
 * {@code preBuildSteps} field; {@code Release} stores them on
 * {@code preBuildSteps} (public-ish); both feed steps into the lazy
 * step closures rendered at synth time, so simply walking
 * {@code workflow.jobs[*].steps} during {@code preSynthesize} misses
 * them. This array enumerates the well-known fields we reach into
 * directly to keep the patch in sync with the centralised
 * {@link VERSION.PNPM_ACTION_SETUP_VERSION}.
 */
const STEP_ARRAY_FIELDS = ["preBuildSteps", "postBuildSteps"] as const;

/**
 * Rewrites every `pnpm/action-setup@vX` step `uses` field reachable from
 * a project's GitHub component (and its sub-projects) to the version
 * pinned in {@link VERSION.PNPM_ACTION_SETUP_VERSION}.
 *
 * Projen's NodeProject and JsiiProject both hardcode a `pnpm/action-setup`
 * pin in their generated build / release / upgrade workflows. When that
 * pin lags behind the version required by the pnpm release we run in CI
 * (e.g. v5 cannot install pnpm 11), the action installs the wrong pnpm
 * version and CI breaks. This helper post-patches every workflow owned
 * by `project.github` so the pin tracks our centralised constant.
 *
 * Two passes run in sequence:
 *
 * 1. **Eager step arrays.** Walks every {@link Component} on the
 *    project (and on its sub-projects) for the well-known fields
 *    enumerated in {@link STEP_ARRAY_FIELDS}. Projen's BuildWorkflow
 *    and Release components hold their pnpm-setup steps in these
 *    arrays before the workflow's lazy step closures resolve them.
 * 2. **Direct workflow jobs.** Walks every job step on every
 *    `GithubWorkflow` for jobs whose steps are eagerly populated
 *    (e.g. the upgrade workflow). Steps held inside lazy closures
 *    are skipped — those are reached via pass 1.
 *
 * @param project - The project whose GitHub workflows should be patched.
 */
export function pinPnpmActionSetup(project: Project): void {
  const pinned = `${PNPM_ACTION_SETUP_PREFIX}${VERSION.PNPM_ACTION_SETUP_VERSION}`;
  // Pass 1: walk components on this project and every sub-project.
  patchComponentStepArrays(project, pinned);
  for (const sub of project.subprojects) {
    patchComponentStepArrays(sub, pinned);
  }
  // Pass 2: walk eagerly-populated workflow job steps on the project's
  // GitHub component (sub-project release workflows attach to the root
  // GitHub via `GitHub.of(project.root)`, so a single pass covers them).
  const github = GitHub.of(project);
  if (!github) {
    return;
  }
  for (const workflow of github.workflows) {
    for (const job of Object.values(workflow.jobs)) {
      patchStepArray(getEagerSteps(job), pinned);
    }
  }
}

/**
 * Walks {@link STEP_ARRAY_FIELDS} on every {@link Component} of the
 * given project and rewrites pnpm/action-setup pins in any step
 * arrays that are eagerly populated.
 */
function patchComponentStepArrays(project: Project, pinned: string): void {
  for (const component of project.components) {
    for (const field of STEP_ARRAY_FIELDS) {
      const steps = readStepArray(component, field);
      if (steps !== undefined) {
        patchStepArray(steps, pinned);
      }
    }
  }
}

/**
 * Reads a {@code JobStep[]}-shaped property off a component, casting
 * past projen's private/readonly declarations. Returns `undefined`
 * when the property is missing or not an array.
 */
function readStepArray(
  component: Component,
  field: string,
): Array<{ uses?: string }> | undefined {
  const value = (component as unknown as Record<string, unknown>)[field];
  return Array.isArray(value) ? (value as Array<{ uses?: string }>) : undefined;
}

/**
 * Reads {@code job.steps} only when it is already an array (i.e. not
 * a lazy closure). Workflows that build their steps via a closure
 * are reached via the component-level pass instead.
 */
function getEagerSteps(job: unknown): Array<{ uses?: string }> | undefined {
  if (typeof job !== "object" || job === null) {
    return undefined;
  }
  const steps = (job as { steps?: unknown }).steps;
  return Array.isArray(steps) ? (steps as Array<{ uses?: string }>) : undefined;
}

/**
 * Mutates a step array in place: rewrites every step whose
 * {@code uses} field starts with the pnpm/action-setup prefix to
 * the pinned version.
 */
function patchStepArray(
  steps: Array<{ uses?: string }> | undefined,
  pinned: string,
): void {
  if (!steps) {
    return;
  }
  for (const step of steps) {
    if (
      typeof step.uses === "string" &&
      step.uses.startsWith(PNPM_ACTION_SETUP_PREFIX)
    ) {
      step.uses = pinned;
    }
  }
}
